import React, { ReactNode, useCallback, useMemo, useRef } from "react";

import { faBucket } from "@fortawesome/pro-solid-svg-icons/faBucket";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityTypeSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { SelectField } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { CreateGroupModal } from "@app/component/createEditEntity/Group";
import useGroupTagRender from "@app/component/EntityTag/useGroupTagRender";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

import styles from "./GroupsSelection.module.scss";

interface Props {
  analyticIssuer?: AnalyticsService;
  className?: string;
  createGroupPosition?: "bottom" | "top";
  disabled?: boolean;
  editableGroupsOnly?: boolean;
  entityType: EntityTypeSupportingGroups;
  groups: Group[];
  label?: ReactNode;
  onChange?: (groupIds: Set<Group["id"]>) => void;
  required?: boolean;
  selectedGroupIds?: Set<Group["id"]>;
}

const GroupsSelection = ({
  analyticIssuer,
  className,
  createGroupPosition = "bottom",
  disabled,
  editableGroupsOnly,
  entityType,
  groups,
  label,
  onChange,
  required,
  selectedGroupIds,
}: Props) => {
  const intl = useIntl();
  const container = useRef<HTMLDivElement>(null);

  const groupOptions = useMemo(
    () =>
      groups
        .filter(
          (group) =>
            (!group.isDynamic && (!editableGroupsOnly || group.accessStatus.update)) ||
            selectedGroupIds?.has(group.id)
        )
        .sort(nameComparator)
        .map(defaultSelectOptionMapper),
    [editableGroupsOnly, groups, selectedGroupIds]
  );

  const handleGroupSelectionChange = useCallback(
    (checkedValues: Array<Group["id"]>) => {
      onChange?.(new Set(checkedValues));
      analyticIssuer?.clicked(["Change Groups"], { groupIds: checkedValues });
    },
    [analyticIssuer, onChange]
  );

  const [newGroupModalVisible, showNewGroupModal, hideNewGroupModal] = useBoolean();
  useModalVisibilityAnalyticEffect("Create New Group", newGroupModalVisible, analyticIssuer);

  const handleHideCreateGroupModel = useCallback(() => hideNewGroupModal(), [hideNewGroupModal]);
  const handleCreateGroup = useCallback(
    (group: Group) => {
      const newSelectedGroupIds = new Set(selectedGroupIds);
      newSelectedGroupIds.add(group.id);
      onChange?.(newSelectedGroupIds);
      analyticIssuer?.clicked(["Create New Group", "Save"], {
        groupId: group.id,
        groupName: group.name,
      });
    },
    [analyticIssuer, onChange, selectedGroupIds]
  );

  useChangeTracking(
    () => analyticIssuer?.clicked(["Create New Group", ...(newGroupModalVisible ? [] : ["Close"])]),
    [analyticIssuer, newGroupModalVisible]
  );

  return (
    <>
      <div className={cn(styles.container, className)} ref={container}>
        <SelectField<Array<Group["id"]>>
          className={styles.select}
          extra={
            createGroupPosition === "top" ? (
              <ButtonLink className={styles.addBtn} disabled={disabled} onClick={showNewGroupModal}>
                {intl.formatMessage({
                  id: "groupsSelection.newGroup.button",
                  defaultMessage: "+ Create New Group",
                  description: "Create new group button in a Group select field",
                })}
              </ButtonLink>
            ) : undefined
          }
          filterOption={defaultFilterOption}
          label={label}
          locked={disabled}
          mode="multiple"
          onChange={handleGroupSelectionChange}
          options={groupOptions}
          placeholder={intl.formatMessage({
            id: "groupsSelection.select.placeholder",
            defaultMessage: "Click or type to select groups",
            description: "Placeholder displayed in a Group select field",
          })}
          prefixIcon={<FontAwesomeIcon icon={faBucket} />}
          required={required}
          tagRender={useGroupTagRender(groups)}
          value={selectedGroupIds ? Array.from(selectedGroupIds) : []}
        />
        {createGroupPosition === "bottom" ? (
          <ButtonLink disabled={disabled} onClick={showNewGroupModal}>
            {intl.formatMessage({
              id: "groupsSelection.newGroup.button",
              defaultMessage: "+ Create New Group",
              description: "Create new group button in a Group select field",
            })}
          </ButtonLink>
        ) : null}
      </div>
      <CreateGroupModal
        entityType={entityType}
        onCreate={handleCreateGroup}
        onHide={handleHideCreateGroupModel}
        visible={newGroupModalVisible}
      />
    </>
  );
};

export default GroupsSelection;
